import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Upload, FileText, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, inr, fmtDate, today, openFile } from "@/lib/api";
import { uploadFile } from "@/lib/compress";
import { StatusBadge, statusLabel } from "@/components/common";

export const STEPS = ["Created", "Loading", "In-Transit", "Delivered"];

function DamagePhotos({ ids }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="damage-photos">
      {ids.map((fid, i) => (
        <button key={fid} type="button" data-testid={`damage-photo-${i}`} onClick={() => openFile(fid).catch((e) => toast.error(errMsg(e)))}
          className="flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-accent">
          <FileText className="h-3.5 w-3.5" /> Damage photo {i + 1}
        </button>
      ))}
    </div>
  );
}

function ProofUpload({ ids, onChange }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { data } = await uploadFile("/uploads?ref_type=document", file);
      onChange([...ids, data.id]);
      toast.success("Photo uploaded");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" onChange={pick} />
      <button type="button" data-testid="damage-upload-btn" disabled={busy || ids.length >= 4} onClick={() => ref.current.click()}
        className="flex items-center gap-1.5 rounded-lg border border-dashed px-3 py-2 text-xs font-semibold text-muted-foreground hover:border-primary hover:text-foreground disabled:opacity-40">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload damage photo ({ids.length}/4)
      </button>
      {ids.map((id, i) => (
        <span key={id} className="flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-[11px]">
          Photo {i + 1}
          <button type="button" data-testid={`damage-remove-${i}`} onClick={() => onChange(ids.filter((x) => x !== id))} className="text-muted-foreground hover:text-rose-600">×</button>
        </span>
      ))}
    </div>
  );
}

export function TripStepper({ booking, onChange, big = false, canBill = false, canDate = false, canEditDates = false, canMarkBill = false }) {
  const [busy, setBusy] = useState(false);
  const [day, setDay] = useState(today());
  const [billDay, setBillDay] = useState(today());
  const [editing, setEditing] = useState(null);
  const [km, setKm] = useState("");
  const [pod, setPod] = useState("ok");
  const [shortQty, setShortQty] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [dmgDesc, setDmgDesc] = useState("");
  const [dmgIds, setDmgIds] = useState([]);
  const [claim, setClaim] = useState("");
  const [deduct, setDeduct] = useState(false);
  const [podReq, setPodReq] = useState(true);
  const { role } = useAuth();
  useEffect(() => { api.get("/company/pod-setting").then((r) => setPodReq(r.data.pod_mandatory)).catch(() => {}); }, []);
  const togglePodReq = async (v) => {
    try { await api.put("/company/pod-setting", { pod_mandatory: v }); setPodReq(v); toast.success(v ? "POD upload is now mandatory" : "POD upload is now optional"); } catch (e) { toast.error(errMsg(e)); }
  };
  const idx = STEPS.indexOf(booking.status);
  const next = STEPS[idx + 1];
  const needKm = !booking.market_trip_id && (next === "Loading" || next === "Delivered");
  const kmLabel = next === "Loading" ? "Trip Start Odometer Reading (Start KM) *" : "Trip End Odometer Reading (End KM) *";
  const [lastKm, setLastKm] = useState(null);
  useEffect(() => {
    if (!needKm || next !== "Loading" || !booking.vehicle_id) { setLastKm(null); return; }
    api.get(`/bookings/${booking.id}/last-km`).then((r) => {
      setLastKm(r.data);
      if (r.data.last_km != null) setKm((k) => (k === "" ? String(r.data.last_km) : k));
    }).catch(() => {});
  }, [booking.id, booking.vehicle_id, needKm, next]);
  const advance = async () => {
    if (needKm && km === "") return toast.error(`Enter ${next === "Loading" ? "Start" : "End"} KM`);
    if (next === "Delivered") {
      if (podReq && !booking.pod_files?.length) return toast.error("Upload the signed POD photo before marking delivered");
      if (pod === "not_ok" && !dmgIds.length) return toast.error("Damage photo is mandatory when POD is NOT OK");
    }
    setBusy(true);
    try {
      if (next === "Delivered") {
        await api.patch(`/bookings/${booking.id}/delivery`, {
          pod_status: pod, shortage_qty: Number(shortQty) || 0, shortage_desc: shortDesc,
          damage_desc: dmgDesc, damage_file_ids: dmgIds,
          claim_amount: Number(claim) || 0, deduct_claim: canBill && deduct,
        });
      }
      await api.patch(`/bookings/${booking.id}/status`, { status: next, odometer_km: needKm ? Number(km) : null, date: canDate ? day : null });
      toast.success(`Marked ${statusLabel(next)}`);
      setKm("");
      setDay(today());
      onChange();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const markBill = async () => {
    const mktFinish = mkt && booking.status !== "Delivered";
    if (mktFinish) {
      if (podReq && !booking.pod_files?.length) return toast.error("Upload the signed POD photo before creating the bill");
      if (pod === "not_ok" && !dmgIds.length) return toast.error("Damage photo is mandatory when POD is NOT OK");
    }
    setBusy(true);
    try {
      if (mktFinish) {
        await api.patch(`/bookings/${booking.id}/delivery`, {
          pod_status: pod, shortage_qty: Number(shortQty) || 0, shortage_desc: shortDesc,
          damage_desc: dmgDesc, damage_file_ids: dmgIds, claim_amount: Number(claim) || 0, deduct_claim: canBill && deduct,
        });
        await api.patch(`/bookings/${booking.id}/status`, { status: "Delivered", odometer_km: null, date: canDate ? billDay : null });
      }
      await api.patch(`/bookings/${booking.id}/bill-created`, { date: billDay }); toast.success("Bill marked as created"); onChange();
    } catch (e) { toast.error(errMsg(e)); onChange(); } finally { setBusy(false); }
  };
  const saveDates = async () => {
    try {
      const { bill, ...dates } = editing;
      await api.patch(`/bookings/${booking.id}/progress-dates`, { dates, bill_date: bill || null });
      toast.success("Dates updated"); setEditing(null); onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const when = (s) => (s === "Bill Created" ? (booking.bill_created_at ? { at: booking.bill_created_at } : null) : booking.status_history?.find((h) => h.status === s));
  const mkt = !!booking.market_trip_id;
  const mktBill = mkt && next === "Delivered" && canMarkBill;
  const shown = mkt ? ["Created", "Loading", "In-Transit", "Bill Created"] : [...STEPS, "Bill Created"];
  const pos = booking.bill_created_at ? shown.length - 1 : mkt ? Math.min(idx, 2) : idx;
  return (
    <div data-testid="trip-stepper">
      <div className="flex items-start">
        {shown.map((s, i) => (
          <div key={s} className="flex flex-1 flex-col items-center text-center">
            <div className="flex w-full items-center">
              <div className={`h-0.5 flex-1 ${i === 0 ? "opacity-0" : i <= pos ? "bg-primary" : "bg-border"}`} />
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold transition-colors ${i <= pos ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground"}`}>
                {i < pos || (i === pos && (booking.status === "Delivered")) ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              <div className={`h-0.5 flex-1 ${i === shown.length - 1 ? "opacity-0" : i < pos ? "bg-primary" : "bg-border"}`} />
            </div>
            {s === "Bill Created" && booking.status === "Delivered" && !booking.bill_created_at
              ? <div className="mt-2 rounded bg-amber-100 px-1.5 text-xs font-semibold text-amber-800" data-testid="step-bill-pending">Bill Pending</div>
              : <div className={`mt-2 text-xs font-semibold ${i <= pos ? "text-foreground" : "text-muted-foreground"}`}>{statusLabel(s)}</div>}
            {when(s) && <div className="text-[10px] text-muted-foreground" data-testid={`step-date-${i}`}>{fmtDate(when(s).at)}</div>}
          </div>
        ))}
      </div>
      {canEditDates && !editing && (
        <div className="mt-2 text-right"><button type="button" data-testid="edit-progress-dates-btn" onClick={() => setEditing(Object.fromEntries(shown.filter(when).filter((s) => canMarkBill || s !== "Bill Created").map((s) => [s === "Bill Created" ? "bill" : s, when(s).at.slice(0, 10)])))} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><Pencil className="h-3 w-3" />Edit dates</button></div>
      )}
      {editing && (
        <div className="mt-3 rounded-lg border p-3" data-testid="progress-dates-panel">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-5">
            {Object.keys(editing).map((k) => (
              <div key={k} className="space-y-1"><label className="text-[11px] font-semibold text-muted-foreground">{k === "bill" ? "Bill Created" : statusLabel(k)}</label>
                <Input type="date" max={today()} data-testid={`progress-date-${k}`} value={editing[k]} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} /></div>
            ))}
          </div>
          <div className="mt-3 flex justify-end gap-2"><Button size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button size="sm" data-testid="progress-dates-save" onClick={saveDates}>Save Dates</Button></div>
        </div>
      )}
      <OdometerSummary booking={booking} canEdit={["admin", "manager", "operation"].includes(role)} onChange={onChange} />
      {next && needKm && (
        <div className="mt-5 space-y-1.5">
          <label className="text-xs font-semibold text-muted-foreground">{kmLabel}</label>
          <Input data-testid="odometer-input" type="number" inputMode="numeric" min="0" value={km} onChange={(e) => setKm(e.target.value)}
            placeholder={next === "Delivered" && booking.trip_start_km != null ? `Start was ${booking.trip_start_km} km` : "e.g. 125430"} className={big ? "h-12 text-base" : ""} />
          {next === "Loading" && lastKm?.last_km != null && km !== "" && Number(km) < lastKm.last_km && (
            <p className="rounded-md bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300" data-testid="km-lower-warning">
              Dhyan dein: Start KM ({Number(km).toLocaleString("en-IN")}) is gaadi ke pichhle End KM ({Number(lastKm.last_km).toLocaleString("en-IN")} km{lastKm.lr_no ? `, ${lastKm.lr_no}` : ""}) se kam hai — galti to nahi?
            </p>
          )}
          {next === "Loading" && lastKm?.last_km != null && (
            <p className="text-xs text-muted-foreground" data-testid="last-km-hint">Auto-filled from last trip {lastKm.lr_no || ""} End KM ({Number(lastKm.last_km).toLocaleString("en-IN")} km) — change if needed</p>
          )}
        </div>
      )}
      {next === "Delivered" && (
        <div className="mt-4 space-y-4 rounded-lg border p-4" data-testid="delivery-panel">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">POD Condition *</label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              {[["ok", "POD OK — no issues"], ["not_ok", "POD NOT OK — damage / shortage"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`pod-cond-${v}`} onClick={() => setPod(v)}
                  className={`min-h-[44px] rounded-lg border px-3 text-sm font-semibold transition-colors ${pod === v ? (v === "ok" ? "border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : "border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300") : "text-muted-foreground hover:border-ring"}`}>
                  {l}
                </button>
              ))}
            </div>
            {podReq && !booking.pod_files?.length && <p className="mt-2 text-xs font-medium text-rose-600">Upload at least one signed POD photo below before delivering.</p>}
            {role === "admin" && (
              <label className="mt-3 flex items-center justify-between gap-3 rounded-md bg-muted/50 px-3 py-2 text-xs font-semibold">
                <span>POD upload mandatory{podReq && <span className="text-rose-600"> *</span>} <span className="font-normal text-muted-foreground">({podReq ? "ON — bina POD Loading Point nahi hoga" : "OFF — POD baad me bhi laga sakte hain"})</span></span>
                <Switch data-testid="pod-mandatory-switch" checked={podReq} onCheckedChange={togglePodReq} />
              </label>
            )}
          </div>
          {pod === "not_ok" && (
            <div className="space-y-3 rounded-lg bg-rose-50/60 p-3 dark:bg-rose-950/20" data-testid="damage-panel">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><label className="text-xs font-semibold text-muted-foreground">Shortage Qty / Weight</label>
                  <Input data-testid="shortage-qty-input" type="number" min="0" value={shortQty} onChange={(e) => setShortQty(e.target.value)} placeholder="Packages or MT missing" /></div>
                <div className="space-y-1.5"><label className="text-xs font-semibold text-muted-foreground">Damage / Shortage Amount (₹)</label>
                  <Input data-testid="claim-amount-input" type="number" min="0" value={claim} onChange={(e) => setClaim(e.target.value)} /></div>
              </div>
              <div className="space-y-1.5"><label className="text-xs font-semibold text-muted-foreground">Shortage Description</label>
                <Input data-testid="shortage-desc-input" value={shortDesc} onChange={(e) => setShortDesc(e.target.value)} placeholder="e.g. 12 cement bags missing" /></div>
              <div className="space-y-1.5"><label className="text-xs font-semibold text-muted-foreground">Damage Item Description</label>
                <Input data-testid="damage-desc-input" value={dmgDesc} onChange={(e) => setDmgDesc(e.target.value)} placeholder="e.g. 5 drums dented and leaking" /></div>
              <div className="space-y-1.5"><label className="text-xs font-semibold text-rose-700 dark:text-rose-300">Damage Photo(s) * (mandatory)</label>
                <ProofUpload ids={dmgIds} onChange={setDmgIds} /></div>
              {canBill && (
                <label className="flex items-center gap-2 text-sm font-medium" data-testid="deduct-claim-row">
                  <input type="checkbox" data-testid="deduct-claim-check" checked={deduct} onChange={(e) => setDeduct(e.target.checked)} className="h-4 w-4 accent-rose-600" />
                  Deduct this amount from the customer bill
                </label>
              )}
            </div>
          )}
        </div>
      )}
      {next && canDate && !mktBill && (
        <div className="mt-4 space-y-1.5">
          <label className="text-xs font-semibold text-muted-foreground">{statusLabel(next)} Date *</label>
          <Input data-testid="status-date-input" type="date" max={today()} value={day} onChange={(e) => setDay(e.target.value)} />
        </div>
      )}
      {next && !mktBill && (
        <Button data-testid="advance-status-btn" onClick={advance} disabled={busy} className={`mt-5 w-full ${big ? "h-14 text-base" : ""}`}>
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {mkt && next === "Delivered" ? "Mark Trip Complete" : `Mark as ${statusLabel(next)}`}
        </Button>
      )}
      {(booking.status === "Delivered" || mktBill) && !booking.bill_created_at && canMarkBill && (
        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-end" data-testid="bill-created-panel">
          <div className="flex-1 space-y-1.5"><label className="text-xs font-semibold text-muted-foreground">Bill Created Date *</label>
            <Input data-testid="bill-created-date-input" type="date" max={today()} value={billDay} onChange={(e) => setBillDay(e.target.value)} /></div>
          <Button data-testid="mark-bill-created-btn" disabled={busy} onClick={markBill}>Mark Bill Created</Button>
        </div>
      )}
    </div>
  );
}

export function DamagePhotosView({ booking: b }) { return <DamagePhotos ids={b.damage_file_ids || []} />; }

function OdometerEdit({ b, onDone }) {
  const [s, setS] = useState(String(b.trip_start_km ?? ""));
  const [e, setE] = useState(b.trip_end_km != null ? String(b.trip_end_km) : "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/bookings/${b.id}/odometer`, { start_km: Number(s), end_km: e === "" ? null : Number(e) });
      toast.success("KM updated"); onDone(true);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-lg border p-3" data-testid="odometer-edit-panel">
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1"><label className="text-[11px] font-semibold text-muted-foreground">Start KM</label>
          <Input type="number" min="0" data-testid="edit-start-km" value={s} onChange={(ev) => setS(ev.target.value)} /></div>
        {b.trip_end_km != null && <div className="space-y-1"><label className="text-[11px] font-semibold text-muted-foreground">End KM</label>
          <Input type="number" min="0" data-testid="edit-end-km" value={e} onChange={(ev) => setE(ev.target.value)} /></div>}
      </div>
      <div className="mt-3 flex justify-end gap-2"><Button size="sm" variant="outline" data-testid="odometer-edit-cancel" onClick={() => onDone(false)}>Cancel</Button>
        <Button size="sm" data-testid="odometer-edit-save" disabled={busy || s === ""} onClick={save}>Save KM</Button></div>
    </div>
  );
}

export function OdometerSummary({ booking: b, canEdit = false, onChange }) {
  const [edit, setEdit] = useState(false);
  if (b.trip_start_km == null) return null;
  const fmt = (v) => (v == null ? "—" : `${Number(v).toLocaleString("en-IN")} km`);
  return (
    <>
    {canEdit && !edit && (
      <div className="mt-4 text-right"><button type="button" data-testid="edit-odometer-btn" onClick={() => setEdit(true)} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><Pencil className="h-3 w-3" />Edit KM</button></div>
    )}
    {edit && <OdometerEdit b={b} onDone={(ok) => { setEdit(false); if (ok) onChange?.(); }} />}
    <div className="mt-2 grid grid-cols-3 gap-2 text-center" data-testid="odometer-summary">
      {[["Start KM", b.trip_start_km], ["End KM", b.trip_end_km], ["Distance Run", b.distance_km]].map(([l, v], i) => (
        <div key={l} className={`rounded-lg p-2.5 ${i === 2 ? "bg-accent text-accent-foreground" : "bg-secondary/60"}`}>
          <div className="label-xs">{l}</div>
          <div className="amount mt-0.5 text-sm font-semibold" data-testid={`odo-${i}`}>{fmt(v)}</div>
        </div>
      ))}
    </div>
    </>
  );
}

export function StopsList({ booking: b }) {
  const pick = b.pickup_points?.length ? b.pickup_points : [{ city: b.pickup_location }];
  const drop = b.drop_points?.length ? b.drop_points : [{ city: b.drop_location }];
  const Row = ({ s, i, kind }) => (
    <div className="flex gap-3 text-sm">
      <div className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${kind === "L" ? "bg-primary" : "bg-emerald-500"}`} />
      <div><span className="font-semibold">{kind === "L" ? "Loading" : "Unloading"} {i + 1}: {s.city}</span>{(s.name || s.address) && <div className="text-xs text-muted-foreground">{[s.name, s.address].filter(Boolean).join(", ")}</div>}</div>
    </div>
  );
  return (
    <div className="space-y-2" data-testid="stops-list">
      {pick.map((s, i) => <Row key={`l${i}`} s={s} i={i} kind="L" />)}
      {drop.map((s, i) => <Row key={`u${i}`} s={s} i={i} kind="U" />)}
    </div>
  );
}

export const EXPENSE_CATS = [["fuel", "Diesel / Fuel"], ["toll", "Toll Tax"], ["allowance", "Salary & Fooding"], ["loading", "Loading"], ["unloading", "Unloading"], ["labour", "Labor Charges"], ["challan", "Police / RTO Challan & Fine"], ["maintenance", "Repairs & Maintenance"], ["parking", "Parking / Detention"], ["other", "Other / Miscellaneous"]];
export const catLabel = (t) => (EXPENSE_CATS.find(([k]) => k === t) || [t, t])[1];
export const catLabelFrom = (cats, t) => (cats.find(([k]) => k === t) || [t, t])[1];

export function useExpenseCats() {
  const [cats, setCats] = useState(EXPENSE_CATS);
  const reload = useCallback(() => api.get("/expense-categories").then((r) => setCats(r.data.filter((c) => c.active).map((c) => [c.key, c.label]))).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);
  return [cats, reload];
}

export function CatSelect({ cats, value, onChange, reloadCats, testid, className }) {
  const { role } = useAuth();
  const canAdd = role === "admin" || role === "manager";
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/expense-categories", { label: name.trim() });
      toast.success(`Category "${data.label}" added`);
      setAdding(false);
      setName("");
      await reloadCats();
      onChange(data.key);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <>
      <select data-testid={testid} className={className} value={value} onChange={(e) => (e.target.value === "__add_new__" ? setAdding(true) : onChange(e.target.value))}>
        {cats.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        {canAdd && <option value="__add_new__" data-testid={`${testid}-add-new`}>+ Add New Expense Type</option>}
      </select>
      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent data-testid="cat-quick-add-dialog">
          <DialogHeader><DialogTitle>Add New Expense Type</DialogTitle><DialogDescription>The new category becomes available to everyone immediately.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <Input data-testid="cat-quick-add-input" autoFocus placeholder="e.g. AdBlue, Tyre, Brokerage" value={name} onChange={(e) => setName(e.target.value)} />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
              <Button type="submit" data-testid="cat-quick-add-save-btn" disabled={busy || !name.trim()}>Add &amp; Select</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
const nowLocal = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };

export function PaidByToggle({ value, onChange, testid = "paid-by" }) {
  return (
    <div className="grid grid-cols-2 gap-2" data-testid={testid}>
      {[["driver", "Paid by Driver"], ["company", "Paid by Company"]].map(([v, l]) => (
        <button key={v} type="button" data-testid={`${testid}-${v}`} onClick={() => onChange(v)}
          className={`min-h-[40px] rounded-lg border px-2 text-xs font-semibold transition-colors ${value === v ? (v === "driver" ? "border-sky-500 bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300" : "border-slate-500 bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200") : "text-muted-foreground hover:border-ring"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

export function ExpenseSummary({ expenses, freight }) {
  const pending = expenses.filter((e) => e.status === "pending").reduce((s, e) => s + e.amount, 0);
  const approved = expenses.filter((e) => e.status === "approved").reduce((s, e) => s + e.amount, 0);
  const profit = (freight || 0) - approved;
  const items = [
    ["Pending Approval", pending, "text-amber-600", "summary-pending"],
    ["Approved Expenses", approved, "text-rose-600", "summary-approved"],
    ...(freight != null ? [
      ["Freight Amount", freight || 0, "", "summary-freight"],
      ["Net Trip Profit", profit, profit >= 0 ? "text-emerald-600" : "text-rose-600", "summary-profit"],
    ] : []),
  ];
  return (
    <div className="mb-4 grid grid-cols-2 gap-2 lg:grid-cols-4" data-testid="expense-summary">
      {items.map(([l, v, c, tid]) => (
        <div key={l} className="rounded-lg bg-secondary/60 p-3">
          <div className="label-xs">{l}</div>
          <div className={`amount mt-0.5 text-sm font-bold ${c}`} data-testid={tid}>{inr(v)}</div>
        </div>
      ))}
    </div>
  );
}


export function ExpenseForm({ bookingId, onAdded, defaultType = "fuel", big = false }) {
  const [f, setF] = useState({ type: defaultType, amount: "", litres: "", date: nowLocal(), note: "", paid_by: "driver" });
  const [receipt, setReceipt] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cats, reloadCats] = useExpenseCats();
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/bookings/${bookingId}/expenses`, { ...f, amount: Number(f.amount), litres: Number(f.litres || 0), receipt_file_id: receipt[0] || null });
      toast.success("Expense submitted");
      setF({ ...f, amount: "", litres: "", note: "", date: nowLocal(), paid_by: "driver" });
      setReceipt([]);
      onAdded();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const h = big ? "h-12 text-base" : "";
  return (
    <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="expense-form">
      <CatSelect testid="expense-type-select" className={`native-select ${big ? "h-12" : ""}`} cats={cats} value={f.type} onChange={(v) => setF({ ...f, type: v })} reloadCats={reloadCats} />
      <Input data-testid="expense-amount-input" className={h} type="number" min="1" required placeholder="Amount ₹" inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
      {f.type === "fuel" && <Input data-testid="expense-litres-input" className={h} type="number" placeholder="Litres" inputMode="decimal" value={f.litres} onChange={(e) => setF({ ...f, litres: e.target.value })} />}
      <Input data-testid="expense-date-input" className={h} type="datetime-local" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
      <div className="col-span-2"><PaidByToggle value={f.paid_by} onChange={(v) => setF({ ...f, paid_by: v })} testid="expense-paid-by" /></div>
      <Input data-testid="expense-note-input" className={`col-span-2 ${h}`} placeholder="Remarks (e.g. Toll paid at Bhopal bypass)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
      <div className="col-span-2" data-testid="expense-receipt"><ReceiptUpload ids={receipt} onChange={(v) => setReceipt(v.slice(-1))} /></div>
      <Button data-testid="expense-submit-btn" disabled={busy} className={`col-span-2 ${big ? "h-12 text-base" : ""}`}>Add Expense</Button>
    </form>
  );
}

function ReceiptUpload({ ids, onChange, testidPrefix = "receipt" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { data } = await uploadFile("/uploads?ref_type=document", file);
      onChange([...ids, data.id]);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" onChange={pick} data-testid={`${testidPrefix}-file-input`} />
      <button type="button" data-testid={`${testidPrefix}-upload-btn`} onClick={() => ref.current.click()}
        className={`flex w-full items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-2.5 text-sm font-semibold ${ids.length ? "border-emerald-400 text-emerald-700 dark:text-emerald-400" : "text-muted-foreground hover:border-primary"}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : ids.length ? <Check className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
        {ids.length ? "Receipt attached — tap to replace" : "Upload bill / receipt photo (optional)"}
      </button>
    </>
  );
}

const REJECT_PRESETS = ["Invalid bill", "Duplicate entry", "Blurry receipt", "Amount mismatch"];

export function ExpenseList({ expenses, canApprove, onChange, canEdit }) {
  const [rejecting, setRejecting] = useState(null);
  const [modifying, setModifying] = useState(null);
  const [editing, setEditing] = useState(null);
  const [reason, setReason] = useState("");
  const [modAmount, setModAmount] = useState("");
  const [modPaidBy, setModPaidBy] = useState("driver");
  const [editForm, setEditForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cats, reloadCats] = useExpenseCats();
  const run = async (fn, msg, close) => {
    setBusy(true);
    try { await fn(); toast.success(msg); close(null); onChange(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const approve = async (id) => {
    try { await api.patch(`/expenses/${id}/status`, { status: "approved" }); toast.success("Bill approved"); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const submitReject = () => {
    if (!reason.trim()) return toast.error("Enter a rejection reason");
    run(() => api.patch(`/expenses/${rejecting.id}/status`, { status: "rejected", reason: reason.trim() }), "Bill rejected", setRejecting);
  };
  const submitModify = () => {
    if (!modAmount || Number(modAmount) <= 0) return toast.error("Enter a valid amount");
    run(() => api.patch(`/expenses/${modifying.id}/status`, { status: "approved", amount: Number(modAmount), paid_by: modPaidBy }), "Approved with modified amount", setModifying);
  };
  const openEdit = (e) => {
    setEditForm({ type: e.type, amount: e.amount, litres: e.litres || "", date: (e.date || "").slice(0, 16), note: e.note || "", paid_by: e.paid_by || "driver", receipt: [] });
    setEditing(e);
  };
  const submitEdit = (ev) => {
    ev.preventDefault();
    const payload = { ...editForm, amount: Number(editForm.amount), litres: Number(editForm.litres || 0) };
    delete payload.receipt;
    if (editForm.receipt.length) payload.receipt_file_id = editForm.receipt[0];
    run(() => api.patch(`/expenses/${editing.id}`, payload), "Expense updated", setEditing);
  };
  if (!expenses.length) return <p className="py-4 text-sm text-muted-foreground">No expenses recorded.</p>;
  return (
    <div className="divide-y" data-testid="expense-list">
      {expenses.map((e) => (
        <div key={e.id} className="flex items-center justify-between gap-3 py-2.5 text-sm" data-testid={`expense-${e.id}`}>
          <div className="min-w-0">
            <div className="font-medium">{catLabelFrom(cats, e.type)}{e.litres ? ` · ${e.litres} L` : ""}{e.vehicle_number && <span className="ml-2 font-mono text-xs text-muted-foreground">{e.vehicle_number}</span>}{e.driver_name && <span className="ml-1 text-xs text-muted-foreground">· {e.driver_name}</span>}</div>
            <div className="truncate text-xs text-muted-foreground">{fmtDate(e.date)} · {e.created_by_name}{e.route && ` · ${e.route}`}{e.note && ` · ${e.note}`}</div>
            {e.reject_reason && <div className="text-xs font-medium text-rose-600" data-testid={`reject-reason-${e.id}`}>Rejected: {e.reject_reason}</div>}
            {e.original_amount != null && <div className="text-xs text-amber-600">Modified from {inr(e.original_amount)}</div>}
          </div>
          <div className="flex items-center gap-2">
            {e.receipt_file_id && <button data-testid={`receipt-view-${e.id}`} onClick={() => openFile(e.receipt_file_id).catch((er) => toast.error(errMsg(er)))} className="rounded border px-2 py-0.5 text-xs font-medium text-primary hover:bg-accent">Bill</button>}
            <span className="amount">{inr(e.amount)}</span>
            <span data-testid={`paid-by-badge-${e.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${e.paid_by === "company" ? "bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300" : "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300"}`}>{e.paid_by === "company" ? "Company Paid" : "Driver Paid"}</span>
            <StatusBadge status={e.status === "pending" ? "pending" : e.status} />
            {canApprove && e.status === "pending" && (
              <>
                <button data-testid={`approve-expense-${e.id}`} onClick={() => approve(e.id)} className="rounded border border-emerald-300 px-2 py-0.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950">Approve</button>
                <button data-testid={`modify-expense-${e.id}`} onClick={() => { setModAmount(e.amount); setModPaidBy(e.paid_by || "driver"); setModifying(e); }} className="rounded border border-amber-300 px-2 py-0.5 text-xs font-semibold text-amber-700 hover:bg-amber-50 dark:text-amber-400">Modify</button>
                <button data-testid={`reject-expense-${e.id}`} onClick={() => { setReason(""); setRejecting(e); }} className="rounded border border-rose-300 px-2 py-0.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950">Reject</button>
              </>
            )}
            {(canApprove || (canEdit && canEdit(e))) && (
              <button data-testid={`edit-expense-${e.id}`} onClick={() => openEdit(e)} title="Edit expense" className="rounded border px-2 py-0.5 text-xs font-semibold text-muted-foreground hover:border-ring hover:text-foreground"><Pencil className="h-3 w-3" /></button>
            )}
          </div>
        </div>
      ))}

      <Dialog open={!!rejecting} onOpenChange={(o) => { if (!o) { setRejecting(null); setReason(""); } }}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader><DialogTitle>Reject expense {rejecting ? `— ${catLabelFrom(cats, rejecting.type)} ${inr(rejecting.amount)}` : ""}</DialogTitle><DialogDescription>Pick a preset reason or type your own. The expense will be excluded from trip calculations.</DialogDescription></DialogHeader>
          <div className="flex flex-wrap gap-2">
            {REJECT_PRESETS.map((p, i) => (
              <button key={p} type="button" data-testid={`reject-preset-${i}`} onClick={() => setReason(p)}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${reason === p ? "border-rose-400 bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300" : "text-muted-foreground hover:border-ring"}`}>{p}</button>
            ))}
          </div>
          <Textarea data-testid="reject-reason-input" placeholder="Reason for rejection (required)" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejecting(null); setReason(""); }}>Cancel</Button>
            <Button data-testid="reject-confirm-btn" disabled={busy} onClick={submitReject} className="bg-rose-600 text-white hover:bg-rose-700">Reject Expense</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!modifying} onOpenChange={(o) => { if (!o) setModifying(null); }}>
        <DialogContent data-testid="modify-dialog">
          <DialogHeader><DialogTitle>Approve with modified amount</DialogTitle><DialogDescription>Enter the approved amount. The original bill amount is kept on record.</DialogDescription></DialogHeader>
          {modifying && <p className="text-sm text-muted-foreground">{catLabelFrom(cats, modifying.type)} · original bill {inr(modifying.amount)}</p>}
          <Input data-testid="modify-amount-input" type="number" min="1" placeholder="Approved amount ₹" value={modAmount} onChange={(e) => setModAmount(e.target.value)} />
          <PaidByToggle value={modPaidBy} onChange={setModPaidBy} testid="modify-paid-by" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setModifying(null)}>Cancel</Button>
            <Button data-testid="modify-confirm-btn" disabled={busy} onClick={submitModify}>Approve ₹{modAmount}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent data-testid="edit-expense-dialog">
          <DialogHeader><DialogTitle>Edit expense {editing && editing.status !== "pending" ? "— saving will send it back to pending approval" : ""}</DialogTitle><DialogDescription>Update the expense type, amount, date, remarks or replace the receipt.</DialogDescription></DialogHeader>
          {editForm && (
            <form onSubmit={submitEdit} className="grid grid-cols-2 gap-3">
              <CatSelect testid="edit-type-select" className="native-select" cats={cats} value={editForm.type} onChange={(v) => setEditForm({ ...editForm, type: v })} reloadCats={reloadCats} />
              <Input data-testid="edit-amount-input" type="number" min="1" required placeholder="Amount ₹" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} />
              {editForm.type === "fuel" && <Input data-testid="edit-litres-input" type="number" placeholder="Litres" value={editForm.litres} onChange={(e) => setEditForm({ ...editForm, litres: e.target.value })} />}
              <Input data-testid="edit-date-input" type="datetime-local" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} />
              <Input data-testid="edit-note-input" className="col-span-2" placeholder="Remarks" value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} />
              <div className="col-span-2"><PaidByToggle value={editForm.paid_by} onChange={(v) => setEditForm({ ...editForm, paid_by: v })} testid="edit-paid-by" /></div>
              <div className="col-span-2 flex items-center gap-2" data-testid="edit-receipt-section">
                {editing?.receipt_file_id && !editForm.receipt.length && (
                  <button type="button" data-testid="edit-receipt-view" onClick={() => openFile(editing.receipt_file_id).catch((er) => toast.error(errMsg(er)))} className="rounded border px-2.5 py-2 text-xs font-medium text-primary hover:bg-accent">View current bill</button>
                )}
                <div className="flex-1"><ReceiptUpload ids={editForm.receipt} onChange={(v) => setEditForm({ ...editForm, receipt: v.slice(-1) })} testidPrefix="edit-receipt" /></div>
              </div>
              <DialogFooter className="col-span-2">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button type="submit" data-testid="edit-save-btn" disabled={busy}>Save Changes</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PodThumb({ pod }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!pod.content_type.startsWith("image/")) return undefined;
    let url;
    api.get(`/files/${pod.file_id}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [pod]);
  return (
    <button type="button" data-testid={`pod-file-${pod.file_id}`} onClick={() => openFile(pod.file_id).catch((e) => toast.error(errMsg(e)))}
      className="group overflow-hidden rounded-lg border bg-secondary/50 text-left transition-colors hover:border-primary">
      <div className="flex aspect-[4/3] items-center justify-center bg-secondary">
        {src ? <img src={src} alt={pod.name} className="h-full w-full object-cover" /> : <FileText className="h-8 w-8 text-muted-foreground" />}
      </div>
      <div className="truncate px-2 py-1.5 text-xs">{pod.name}</div>
    </button>
  );
}

export function PodSection({ booking, onChange, big = false }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      await uploadFile(`/bookings/${booking.id}/pod`, file);
      toast.success("POD uploaded");
      onChange();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div data-testid="pod-section">
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} data-testid="pod-file-input" />
      <Button type="button" variant="outline" data-testid="pod-upload-btn" disabled={busy} onClick={() => ref.current.click()} className={`w-full border-dashed ${big ? "h-14 text-base" : ""}`}>
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} {busy ? "Uploading…" : "Upload POD (photo / PDF)"}
      </Button>
      {booking.pod_files?.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">{booking.pod_files.map((p) => <PodThumb key={p.file_id} pod={p} />)}</div>
      )}
    </div>
  );
}
